import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import React from "react";
import uploadService from "../../../services/upload.service";
import { filterNullableArray } from "../../../utils/type.util";
import { validateTaxFile } from "./taxFile";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

export type TaxUploadStatus = "uploading" | "uploaded" | "failed" | "rejected";

export interface TaxUpload {
  id: string;
  file: File;
  status: TaxUploadStatus;
  progress: number;
  reason?: string;
  uploadedFileId?: number;
}

type PolledFile = NonNullable<
  NonNullable<GraphQL.UploadFilesByPollCollectionQuery["uploadFilesPollByCollection"]>[number]
>;

function toUploadingFile(file: File): GraphQL.UploadingFilesInput {
  return { fileName: file.name, fileSize: file.size, contentType: file.type, taxDocument: true };
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}`;
}

/** Drops files already staged (by name + size) and duplicates within the same selection. */
function pickNewFiles(incoming: File[], existing: TaxUpload[]): File[] {
  const seen = new Set(existing.map((upload) => fileKey(upload.file)));
  return incoming.filter((file) => {
    const key = fileKey(file);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Drives the upload-on-select flow: each selected file uploads immediately and
 * independently (create → transfer → poll), tracked as its own row. The shared
 * useUploadService is batch-oriented, so this view owns its own per-file pipeline.
 */
export function useTaxExemptUpload(customerPublicId: string) {
  const [uploads, setUploads] = React.useState<TaxUpload[]>([]);
  const nextId = React.useRef(0);
  // Rows removed while still uploading: their in-flight runUpload deletes the finished doc on resolve.
  const discarded = React.useRef<Set<string>>(new Set());

  const createUpload = useRpiGraphqlRequest<GraphQL.CreateUploadMutation, GraphQL.CreateUploadMutationVariables>(
    GraphQL.CreateUploadDocument
  );
  const pollCollection = useRpiGraphqlRequest<
    GraphQL.UploadFilesByPollCollectionQuery,
    GraphQL.UploadFilesByPollCollectionQueryVariables
  >(GraphQL.UploadFilesByPollCollectionDocument);
  const deleteUploadedFile = useRpiGraphqlRequest<
    GraphQL.UploadedFileDeleteMutation,
    GraphQL.UploadedFileDeleteMutationVariables
  >(GraphQL.UploadedFileDeleteDocument);

  const patch = React.useCallback((id: string, change: Partial<TaxUpload>) => {
    setUploads((current) => current.map((upload) => (upload.id === id ? { ...upload, ...change } : upload)));
  }, []);

  // Best-effort: a failed delete leaves an orphaned upload but must not block the UI, so we log rather than throw.
  const deleteUploaded = React.useCallback(
    (id: number) =>
      deleteUploadedFile({ id }).catch((error) => {
        console.warn("Failed to delete uploaded tax-exempt document.", error);
      }),
    [deleteUploadedFile]
  );

  const awaitTerminalStatus = React.useCallback(
    async (collectionId: string, attemptsLeft = MAX_POLL_ATTEMPTS): Promise<PolledFile | undefined> => {
      const data = await pollCollection({ collectionId, customerPublicId });
      const [file] = filterNullableArray(data.uploadFilesPollByCollection ?? []);
      if (file && file.status !== GraphQL.UploadedFileStatus.Started) return file;
      if (attemptsLeft <= 0) return undefined;
      await delay(POLL_INTERVAL_MS);
      return awaitTerminalStatus(collectionId, attemptsLeft - 1);
    },
    [pollCollection, customerPublicId]
  );

  const runUpload = React.useCallback(
    async (id: string, file: File) => {
      try {
        const created = await createUpload({ input: { customerPublicId, files: [toUploadingFile(file)] } });
        const collection = created.uploadCreate;
        if (!collection) throw new Error("Upload could not be created.");

        await uploadService.uploadCollection.observe({ bytes: (value) => patch(id, { progress: value }) })(collection, [
          file,
        ]);

        const result = await awaitTerminalStatus(collection.collectionId);

        if (discarded.current.delete(id)) {
          if (result?.status === GraphQL.UploadedFileStatus.Finished) void deleteUploaded(result.id);
          return;
        }

        if (result?.status === GraphQL.UploadedFileStatus.Finished) {
          patch(id, { status: "uploaded", progress: 1, uploadedFileId: result.id });
        } else {
          patch(id, { status: "failed" });
        }
      } catch {
        if (discarded.current.delete(id)) return;
        patch(id, { status: "failed" });
      }
    },
    [createUpload, customerPublicId, patch, awaitTerminalStatus, deleteUploaded]
  );

  const addFiles = React.useCallback(
    (files: File[]) => {
      const additions = pickNewFiles(files, uploads).map<TaxUpload>((file) => {
        const id = `tax-upload-${nextId.current++}`;
        const validation = validateTaxFile(file);
        if (validation.ok) return { id, file, status: "uploading", progress: 0 };
        return { id, file, status: "rejected", progress: 0, reason: validation.reason };
      });
      if (additions.length === 0) return;
      setUploads((current) => [...current, ...additions]);
      additions.forEach((upload) => {
        if (upload.status === "uploading") void runUpload(upload.id, upload.file);
      });
    },
    [uploads, runUpload]
  );

  const retry = React.useCallback(
    (id: string) => {
      const target = uploads.find((upload) => upload.id === id);
      if (!target) return;
      patch(id, { status: "uploading", progress: 0, reason: undefined, uploadedFileId: undefined });
      void runUpload(id, target.file);
    },
    [uploads, patch, runUpload]
  );

  const remove = React.useCallback(
    (id: string) => {
      const target = uploads.find((upload) => upload.id === id);
      setUploads((current) => current.filter((upload) => upload.id !== id));
      if (target?.uploadedFileId !== undefined) {
        void deleteUploaded(target.uploadedFileId);
      } else if (target?.status === "uploading") {
        discarded.current.add(id);
      }
    },
    [uploads, deleteUploaded]
  );

  const discardAll = React.useCallback(async () => {
    const uploadedIds = uploads.flatMap((upload) =>
      upload.uploadedFileId !== undefined ? [upload.uploadedFileId] : []
    );
    uploads.forEach((upload) => {
      if (upload.status === "uploading" && upload.uploadedFileId === undefined) discarded.current.add(upload.id);
    });
    setUploads([]);
    await Promise.all(uploadedIds.map(deleteUploaded));
  }, [uploads, deleteUploaded]);

  const isUploading = uploads.some((upload) => upload.status === "uploading");
  const hasUploaded = uploads.some((upload) => upload.status === "uploaded");
  const hasFailed = uploads.some((upload) => upload.status === "failed");

  return { uploads, addFiles, retry, remove, discardAll, isUploading, hasUploaded, hasFailed };
}
